// src/utils/logger.js
// Simple logging utility for Svelte applications

// Log levels
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// Current log level (can be changed at runtime)
let currentLevel = LOG_LEVELS.INFO;

// Whether to show timestamps in logs
let showTimestamp = true;

// Whether to enable storing logs to localStorage
let storeLogsInLocalStorage = false;
const LOCAL_STORAGE_KEY = "app_logs";
const MAX_STORED_LOGS = 1000;

// Format the log message with timestamp if enabled
function formatMessage(level, message, data) {
  const timestamp = showTimestamp ? `[${new Date().toISOString()}] ` : "";
  const levelStr = `[${level}] `;

  // If data is provided, stringify it
  const dataStr = data ? ` ${JSON.stringify(data, null, 2)}` : "";

  return `${timestamp}${levelStr}${message}${dataStr}`;
}

// Store log in localStorage if enabled
function storeLog(level, message, data) {
  if (!storeLogsInLocalStorage) return;

  try {
    // Get existing logs
    const storedLogs = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_KEY) || "[]",
    );

    // Add new log
    storedLogs.push({
      timestamp: new Date().toISOString(),
      level,
      message,
      data: data || null,
    });

    // Keep only the most recent logs
    const trimmedLogs = storedLogs.slice(-MAX_STORED_LOGS);

    // Save back to localStorage
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(trimmedLogs));
  } catch (error) {
    console.error("Failed to store log in localStorage:", error);
  }
}

// The logger object
const logger = {
  // Set the current log level
  setLevel(level) {
    if (LOG_LEVELS[level] !== undefined) {
      currentLevel = LOG_LEVELS[level];
    } else if (typeof level === "number" && level >= 0 && level <= 3) {
      currentLevel = level;
    } else {
      console.warn(`Invalid log level: ${level}. Using INFO level.`);
      currentLevel = LOG_LEVELS.INFO;
    }
    return this;
  },

  // Toggle timestamp display
  setShowTimestamp(show) {
    showTimestamp = !!show;
    return this;
  },

  // Enable/disable localStorage logging
  setStoreLogsInLocalStorage(enable) {
    storeLogsInLocalStorage = !!enable;
    return this;
  },

  // Clear stored logs
  clearStoredLogs() {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear stored logs:", error);
    }
    return this;
  },

  // Get stored logs
  getStoredLogs() {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "[]");
    } catch (error) {
      console.error("Failed to retrieve stored logs:", error);
      return [];
    }
  },

  // Debug level logging
  debug(message, data) {
    if (currentLevel <= LOG_LEVELS.DEBUG) {
      console.debug(formatMessage("DEBUG", message, data));
      storeLog("DEBUG", message, data);
    }
    return this;
  },

  // Info level logging
  info(message, data) {
    if (currentLevel <= LOG_LEVELS.INFO) {
      console.info(formatMessage("INFO", message, data));
      storeLog("INFO", message, data);
    }
    return this;
  },

  // Warning level logging
  warn(message, data) {
    if (currentLevel <= LOG_LEVELS.WARN) {
      console.warn(formatMessage("WARN", message, data));
      storeLog("WARN", message, data);
    }
    return this;
  },

  // Error level logging
  error(message, data) {
    if (currentLevel <= LOG_LEVELS.ERROR) {
      console.error(formatMessage("ERROR", message, data));
      storeLog("ERROR", message, data);
    }
    return this;
  },

  // Group related logs (passes through to console.group)
  group(label) {
    console.group(label);
    return this;
  },

  // End a log group (passes through to console.groupEnd)
  groupEnd() {
    console.groupEnd();
    return this;
  },
};

export default logger;
